import type {
  BoxContentsData,
  BoxListItem,
  BoxProductItem,
  BoxesPageInput,
  CreateBoxInput,
  DeletedBoxItem,
  PagedResult,
  UpdateBoxInput,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeBoxListItem = (value: unknown) => BoxListItem;
type NormalizeBoxProductItem = (value: unknown) => BoxProductItem;
type NormalizeDeletedBoxItem = (value: unknown) => DeletedBoxItem;
type NormalizeTypeTags = (value: unknown) => string[];
type ToSafeNonNegativeInt = (value: unknown, fallback: number) => number;
type NormalizePagedPayload = <T>(
  payload: unknown,
  mapItem: (item: unknown) => T,
  fallbackErrorMessage: string,
) => PagedResult<T>;

interface BoxApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeBoxListItem: NormalizeBoxListItem;
  normalizeBoxProductItem: NormalizeBoxProductItem;
  normalizeDeletedBoxItem: NormalizeDeletedBoxItem;
  normalizeTypeTags: NormalizeTypeTags;
  toSafeNonNegativeInt: ToSafeNonNegativeInt;
  normalizePagedPayload: NormalizePagedPayload;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export class BoxApiModule {
  constructor(
    private readonly context: Pick<
      WarehouseApiModuleContext,
      "getJson" | "sendJson" | "fetchWithAuth"
    >,
    private readonly deps: BoxApiModuleDependencies,
  ) {}

  async getAllBoxes(): Promise<BoxListItem[]> {
    const page = await this.getBoxesPage();
    return page.items;
  }

  async getBoxesPage(
    input: BoxesPageInput = {},
  ): Promise<PagedResult<BoxListItem>> {
    const query = new URLSearchParams();

    if (typeof input.limit === "number" && Number.isFinite(input.limit)) {
      query.set("limit", String(Math.max(1, Math.floor(input.limit))));
    }

    if (typeof input.offset === "number" && Number.isFinite(input.offset)) {
      query.set("offset", String(Math.max(0, Math.floor(input.offset))));
    }

    if (typeof input.q === "string" && input.q.trim()) {
      query.set("q", input.q.trim());
    }

    if (typeof input.typeTag === "string" && input.typeTag.trim()) {
      query.set("typeTag", input.typeTag.trim());
    }

    const path = query.toString()
      ? `/api/boxes?${query.toString()}`
      : "/api/boxes";
    const payload = await this.context.getJson<unknown>(path);

    return this.deps.normalizePagedPayload(
      payload,
      (item) => this.deps.normalizeBoxListItem(item),
      "Некорректный ответ сервера при загрузке коробок",
    );
  }

  async getBoxById(id: string): Promise<BoxListItem | null> {
    const response = await this.context.fetchWithAuth(
      `/api/boxes/${encodeURIComponent(id)}`,
      "GET",
    );

    if (response.status === 404) {
      return null;
    }

    const payload = (await response.json()) as unknown;
    if (
      !response.ok ||
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке коробки",
      );
    }

    return this.deps.normalizeBoxListItem(payload.data);
  }

  async searchBoxes(query: string): Promise<BoxListItem[]> {
    const payload = await this.context.getJson<unknown>(
      `/api/boxes/search?q=${encodeURIComponent(query)}`,
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при поиске коробок",
      );
    }

    return payload.data.map((item) => this.deps.normalizeBoxListItem(item));
  }

  async createBox(input: CreateBoxInput): Promise<BoxListItem> {
    const typeTags = this.deps.normalizeTypeTags(input.typeTags);
    const payload = await this.context.sendJson<unknown>("/api/boxes", "POST", {
      name: input.name,
      photo: input.photo || "",
      description: input.description || "",
      typeTags,
      parentId: input.parentId ?? null,
    });

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при создании коробки",
      );
    }

    return this.deps.normalizeBoxListItem(payload.data);
  }

  async updateBox(id: string, input: UpdateBoxInput): Promise<BoxListItem> {
    const typeTags = this.deps.normalizeTypeTags(input.typeTags);
    const payload = await this.context.sendJson<unknown>(
      `/api/boxes/${encodeURIComponent(id)}`,
      "PUT",
      {
        name: input.name,
        photo: input.photo || "",
        description: input.description || "",
        typeTags,
        parentId: input.parentId ?? null,
      },
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при обновлении коробки",
      );
    }

    return this.deps.normalizeBoxListItem(payload.data);
  }

  async deleteBox(id: string, deleteReason?: string | null): Promise<void> {
    const normalizedReason = (deleteReason || "").trim();
    const payload = await this.context.sendJson<unknown>(
      `/api/boxes/${encodeURIComponent(id)}`,
      "DELETE",
      normalizedReason ? { deleteReason: normalizedReason } : undefined,
    );
    this.ensureSuccess(payload, "Не удалось удалить коробку");
  }

  async getBoxContents(boxId: string): Promise<BoxContentsData> {
    const payload = await this.context.sendJson<unknown>(
      `/api/boxes/${encodeURIComponent(boxId)}/contents`,
      "GET",
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload) ||
      !isRecord(payload.data) ||
      !("contents" in payload.data) ||
      !isRecord(payload.data.contents) ||
      !Array.isArray(payload.data.contents.boxes) ||
      !Array.isArray(payload.data.contents.products)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError ||
          "Некорректный ответ сервера при загрузке содержимого коробки",
      );
    }

    return {
      box: this.deps.normalizeBoxListItem(payload.data.box),
      contents: {
        boxes: payload.data.contents.boxes.map((item) =>
          this.deps.normalizeBoxListItem(item),
        ),
        products: payload.data.contents.products.map((item) =>
          this.deps.normalizeBoxProductItem(item),
        ),
      },
    };
  }

  async getDeletedBoxes(
    input: { limit?: number; offset?: number } = {},
  ): Promise<PagedResult<DeletedBoxItem>> {
    const query = new URLSearchParams();
    if (typeof input.limit === "number" && Number.isFinite(input.limit)) {
      query.set("limit", String(Math.max(1, Math.floor(input.limit))));
    }
    if (typeof input.offset === "number" && Number.isFinite(input.offset)) {
      query.set("offset", String(Math.max(0, Math.floor(input.offset))));
    }

    const path = query.toString()
      ? `/api/boxes/deleted?${query.toString()}`
      : "/api/boxes/deleted";
    const payload = await this.context.getJson<unknown>(path);

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !isRecord(payload.data) ||
      !Array.isArray(payload.data.items)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке удаленных коробок",
      );
    }

    return {
      items: payload.data.items.map((item) =>
        this.deps.normalizeDeletedBoxItem(item),
      ),
      total: this.deps.toSafeNonNegativeInt(payload.data.total, 0),
      offset: this.deps.toSafeNonNegativeInt(payload.data.offset, 0),
      limit:
        payload.data.limit === null || payload.data.limit === undefined
          ? null
          : this.deps.toSafeNonNegativeInt(payload.data.limit, 0),
    };
  }

  async restoreBox(id: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/boxes/${encodeURIComponent(id)}/restore`,
      "POST",
    );
    this.ensureSuccess(payload, "Не удалось восстановить коробку");
  }

  async hardDeleteBox(id: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/boxes/${encodeURIComponent(id)}/hard-delete`,
      "DELETE",
    );
    this.ensureSuccess(payload, "Не удалось удалить коробку окончательно");
  }

  private ensureSuccess(payload: unknown, fallbackMessage: string): void {
    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || fallbackMessage);
    }
  }
}
